import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'

import { rscStore } from './stores/rscStore.js'
import { attributesStore } from './stores/attributesStore.js'
import { userSelectionStore } from './stores/userSelectionStore.js'
import { covid19Store } from './stores/covid19Store.js'

Vue.use(Vuex)

export const store = new Vuex.Store({

  modules: {
    rsc: rscStore,
    attributes: attributesStore,
    selections: userSelectionStore,
    covid19: covid19Store
  },

  state: {
    /**
     * Tells if the regime navigation is available on the current page
     */
    regime_nav_enabled: false,

    /**
     * Is the regime selected by the user
     * We default to yes!
     */
    regime_enabled: true,

    showTourTooltip: false,

    image_download_path: 'https://www.idea.int/nodejs',
    pathSelection: {},

    mousePosition: { x: 0, y: 0 },

    windowSize: { width: 0, height: 0 },
    contentSize: { width: 0, height: 0 },

    /**
     * Use to tell if the application is fetching something or not
     * @type {Boolean}
     */
    fetching: [],

    /**
     * Current slider value
     * @type {Array}
     */
    defaultYears: [1975, 2021],

    /**
     * Current slider value
     * @type {Array}
     */
    yearSliderCurrent: 2021,

    /**
     * Current slider value
     * @type {Array}
     */
    rangeYearSliderCurrent: [1975, 2021],

    /**
     * Currently displayed page tutorial
     * @type {Boolean|String}
     */
    pagetutorial: false,

    /**
     * The colors to be used in charts
     * @type {Array}
     */
    colors: [
      {
        'name': 'blue',
        'hex': '#00ADE4'
      },
      {
        'name': 'orange',
        'hex': '#EF5214'
      },
      {
        'name': 'red',
        'hex': '#E21F55'
      },
      {
        'name': 'green',
        'hex': '#85BC4F'
      },
      {
        'name': 'palegreen',
        'hex': '#74C19D'
      },
      {
        'name': 'pink',
        'hex': '#ea6cae'
      },
      {
        'name': 'purple',
        'hex': '#50479E'
      },
      {
        'name': 'yellow',
        'hex': '#f3d139'
      },
      {
        'name': 'gray-lightest',
        'hex': '#F1F3F7'
      },
      {
        'name': 'gray-lighter',
        'hex': '#DCE1E5'
      },
      {
        'name': 'gray-light',
        'hex': '#BEC4CC'
      },
      {
        'name': 'gray',
        'hex': '#9AA1A8'
      },
      {
        'name': 'gray-darker',
        'hex': '#7E8489'
      },
      {
        'name': 'gray-darkest',
        'hex': '#2F3E5E'
      },
      {
        'name': 'white',
        'hex': '#FFF'
      },
      {
        'name': 'disabled',
        'hex': '#989da3'
      },
      {
        'name': 'valuered',
        'hex': '#E21F55'
      },
      {
        'name': 'valueyellow',
        'hex': '#f3d139'
      },
      {
        'name': 'valuegreen',
        'hex': '#85BC4F'
      },
      {
        'name': 'regime-purple',
        'hex': '#A78AC2'
      },
      {
        'name': 'regime-orange',
        'hex': '#F79C32'
      },
      {
        'name': 'regime-dem-high',
        'hex': '#2BCBA8'
      },
      {
        'name': 'regime-dem-mid',
        'hex': '#7FE0CB'
      },
      {
        'name': 'regime-dem-low',
        'hex': '#BFEFE5'
      }
    ],

    /**
     * The user selected viewmode
     * @type {String}
     */
    viewMode: 'chart',

    /**
     * The user selected confidenceInterval
     * @type {Boolean}
     */
    confidenceIntervals: true,

    /**
     * world average
     * @type {Boolean}
     */
    isWorldAverageEnabled: true,

    colorMapping: [],

    helptoolbars: [
      {
        'path': '/indices/compare-attributes',
        'description': 'Lorem ipsum dolor sit amet, consectetur.',
        'popupTitle': 'Nam voluptatibus',
        'popupContent': 'Officiis, est, quas veniam repellendus error quasi assumenda dicta at nesciunt sunt, reprehenderit odio consequuntur magni laborum ipsam aliquid, accusantium delectus. Atque.'
      },
      {
        'path': '/indices/countries-regions-profile',
        'description': 'Lorem ipsum dolor sit amet, consectetur adipisicing.',
        'popupTitle': 'Wuas veniam repellendus',
        'popupContent': 'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Nam voluptatibus animi totam aperiam necessitatibus iste minus veniam obcaecati nemo porro repellendus tenetur assumenda tempore doloribus dicta, magni rem. Officiis, est, quas veniam repellendus error quasi assumenda dicta at nesciunt sunt, reprehenderit odio consequuntur magni laborum ipsam aliquid, accusantium delectus. Atque.'
      }
    ]
  },

  getters: {
    /**
     * Maps a key to a color
     * @param  {obj} state    The Vuex state
     * @param  {obj} getters) The Vuex getters
     * @return {string}       Color name
     */
    getSelectionColor: (state, getters) => (key) => {
      key = String(key)
      let alreadyAvailable = _.find(state.colorMapping, { key: key })

      if (alreadyAvailable) { return alreadyAvailable.color }

      let usedColors = _.map(state.colorMapping, 'color')

      // Get unique color from color array
      let availableColors = state.colors.slice(0, 7)
      let color = _.find(availableColors, (color) => {
        return usedColors.indexOf(color.name) === -1
      })

      // If no color - return a default
      if (!color) {
        color = _.first(availableColors)
      }

      state.colorMapping.push({
        key: key,
        color: color.name
      })

      return color.name
    }
  },

  mutations: {
    /**
     * Resets color section
     * @param  {obj} state    The Vuex state
     * @param  {obj} getters) The Vuex getters
     * @return {string}       Color name
     */
    resetSelectionColor (state) {
      state.colorMapping = []
    }

  },

  actions: {
    /**
     * Select or unselect a country
     * @param  {obj} options.state     Vuex state
     * @param  {obj} options.commit    Vuex commit
     * @param  {obj} options.rootState Vuex rootState
     * @param  {string} key            A key for the fetching
     * @return {void}
     */
    startFetching ({ state, commit, rootState }, key) {
      state.fetching.push({ id: key })
    },

    /**
     * Select or unselect a country
     * @param  {obj} options.state     Vuex state
     * @param  {obj} options.commit    Vuex commit
     * @param  {obj} options.rootState Vuex rootState
     * @param  {string} key            A key for the fetching
     * @return {void}
     */
    stopFetching ({ state, commit, rootState }, key) {
      let i = _.findIndex(state.fetching, ['id', key])
      if (i !== -1) {
        state.fetching.splice(i, 1)
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/store.js